import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import posthog from 'posthog-js';
import { APP_CONFIG } from './app/app-config.injection-token';
import { AppModule } from './app/app.module';
import { getDataAttributesFromAppRoot } from './app/functions/utilities/get-data-attributes-from-approot';
import { AppConfig } from './app/model/app-config';
import { environment } from './environments/environment';

import { LicenseManager } from '@ag-grid-enterprise/core';
LicenseManager.setLicenseKey(environment.agGridLicenseKey);

let appConfigURI = '/assets/data/appConfig.json';

if (environment.production) {
  appConfigURI = '/api/config';
  enableProdMode();
}

const fetchWithRetry = async (uri: string, retries = 1): Promise<AppConfig> => {
  for (let i = 0; i <= retries; i++) {
    try {
      // Append timestamp on consecutive requests to avoid caching
      const requestUri = i === 0 ? uri : `${uri}?_t=${Date.now()}`;
      const response = await fetch(requestUri);
      const data = await response.json();

      // Check if the response is an empty object
      if (Object.keys(data).length === 0) {
        throw new Error('Empty configuration received');
      }

      return data;
    } catch (error) {
      if (i === retries) throw error;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
  }
  throw new Error('Failed to fetch configuration after all retries');
};

fetchWithRetry(appConfigURI)
  .then((appConfig: AppConfig) => {
    if (appConfig.posthog !== undefined) {
      posthog.init(appConfig.posthog.token, {
        api_host: appConfig.posthog.apiHost,
        ui_host: appConfig.posthog.uiHost,
        persistence: 'memory',
        capture_pageview: 'history_change',
        capture_pageleave: true,
        enable_heatmaps: false,
      });
      /**
       * To enable or disable posthog debugging,
       * you need to uncomment the line below and
       * restart the dev server to make it take effect
       */
      // posthog.debug( true | false );
    }
    if (appConfig.faro !== undefined) {
      initializeFaro({
        url: appConfig.faro.collectorUrl,
        app: {
          name: 'ClimateOS',
          version: appConfig.faro.version,
          environment: appConfig.faro.environment,
        },
        sessionTracking: {
          enabled: true,
          persistent: true,
          maxSessionPersistenceTime: 30 * 60 * 1000, // 30 minutes, same as GA.
        },
        instrumentations: [
          // Mandatory, overwriting the instrumentations array would cause the default instrumentations to be omitted
          ...getWebInstrumentations(),

          // Initialization of the tracing package.
          // This packages is optional because it increases the bundle size noticeably. Only add it if you want tracing data.
          new TracingInstrumentation({
            instrumentationOptions: {
              propagateTraceHeaderCorsUrls: [new RegExp(appConfig.azure.proxy_base_url + '/*')],
            },
          }),
        ],
      });
    }

    platformBrowserDynamic([
      {
        provide: APP_CONFIG,
        useFactory: () => {
          const data: any = getDataAttributesFromAppRoot();

          if (data.boardId && data.publishedVersion) {
            return {
              ...appConfig,
              customHosting: {
                boardId: data.boardId,
                publishedVersion: data.publishedVersion,
              },
            };
          }
          return appConfig;
        },
      },
    ])
      .bootstrapModule(AppModule)
      .catch((err) => {
        console.error(err);
        (document.querySelector('.loader') as HTMLDivElement).style.display = 'none';
      });
  })
  .catch((err) => {
    console.error(err);
    (document.querySelector('.loader') as HTMLDivElement).style.display = 'none';
    (document.querySelector('.load-error') as HTMLDivElement).style.display = 'block';
  });
